import { useEffect, useState } from "preact/hooks";
import { ApiError, api, type Wardrobe } from "./api";
import { DEFAULT_HAIR, DEFAULT_SHIRT, isHairstyle, isShirt, type Hairstyle, type PeonAppearance, type Shirt } from "../shared/shirts.ts";
import { DEFAULT_BEARD, isBeard, normalizeAccessories } from "../shared/cosmetics.ts";
import { normalizeWornBadges, type AchievementKey } from "../shared/achievements.ts";
import { markLocalLook, nextLookGeneration, resetLookRowsForTests, rowIsFresh, setLookRowSink, type RowLook } from "./look-rows.ts";

/**
 * How each peon looks (shared/shirts.ts, shared/cosmetics.ts, docs/peon-look.md), by name, for the
 * life of the page: the shirt, the hair, the beard, the accessories and the badges pinned on the chest. The account sheet and the
 * character editor (#/peon) read the signed-in peon's look from here; the editor writes a change here
 * first and to the server behind (PUT /api/peons/appearance with the fields that changed), so it
 * shows at once. A name never looked up is fetched once; until it answers, and if it fails, the
 * defaults show (brown, nothing else).
 *
 * The board summary and the Hall carry looks beside names (TopPeon, HallMonth). Those rows reach the
 * store as their requests answer (src/api.ts), and only when the request started after this device's
 * own last change to that peon (src/look-rows.ts): a summary already on its way, or a board painted
 * from memory, never puts the old clothes back over the ones just chosen (owner, 2026-10-04: "The
 * peon of the month card on the done screen seems to be behind on clothing updates").
 *
 * Beside the looks, the signed-in peon's wardrobe (GET /api/peons/wardrobe): the pieces owned, the
 * gold held and whether the shop is selling, fetched when the editor opens and replaced after a buy.
 */
interface Look {
  shirt?: Shirt;
  hair?: Hairstyle;
  beard?: string;
  accessories?: string[];
  badges?: AchievementKey[];
}
const looks = new Map<string, Look>();
const pending = new Set<string>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function key(name: string): string {
  return name.trim().toLowerCase();
}

export function getShirt(name: string): Shirt {
  return looks.get(key(name))?.shirt ?? DEFAULT_SHIRT;
}

export function getHair(name: string): Hairstyle {
  return looks.get(key(name))?.hair ?? DEFAULT_HAIR;
}

export function getBeard(name: string): string {
  return looks.get(key(name))?.beard ?? DEFAULT_BEARD;
}

export function getAccessories(name: string): string[] {
  return looks.get(key(name))?.accessories ?? [];
}

/** The badge worn over the shirt's patch (Your peon's Badges section): a list of at most one. */
export function getBadges(name: string): AchievementKey[] {
  return looks.get(key(name))?.badges ?? [];
}

const DEFAULT_LOOK: PeonAppearance = { shirt: DEFAULT_SHIRT, hair: DEFAULT_HAIR, beard: DEFAULT_BEARD, accessories: [], badges: [] };

export function getLook(name: string): PeonAppearance {
  return { shirt: getShirt(name), hair: getHair(name), beard: getBeard(name), accessories: getAccessories(name), badges: getBadges(name) };
}

function sameLook(a: Look | PeonAppearance | undefined, b: Look | PeonAppearance): boolean {
  return (
    !!a &&
    a.shirt === b.shirt &&
    a.hair === b.hair &&
    a.beard === b.beard &&
    (a.accessories ?? []).join() === (b.accessories ?? []).join() &&
    (a.badges ?? []).join() === (b.badges ?? []).join()
  );
}

/** A look as the server sent it, every part checked: what this build does not know reads as the default, a part not sent stays unset. */
function fromServer(look: Partial<Record<keyof PeonAppearance, unknown>>): Look {
  const out: Look = {};
  if (look.shirt !== undefined) out.shirt = isShirt(look.shirt) ? look.shirt : DEFAULT_SHIRT;
  if (look.hair !== undefined) out.hair = isHairstyle(look.hair) ? look.hair : DEFAULT_HAIR;
  if (look.beard !== undefined) out.beard = isBeard(look.beard) ? look.beard : DEFAULT_BEARD;
  if (look.accessories !== undefined) out.accessories = normalizeAccessories(Array.isArray(look.accessories) ? look.accessories : []);
  if (look.badges !== undefined) out.badges = normalizeWornBadges(Array.isArray(look.badges) ? look.badges : []);
  return out;
}

function load(name: string): void {
  const k = key(name);
  if (!k || looks.has(k) || pending.has(k)) return;
  pending.add(k);
  const gen = nextLookGeneration();
  api.peons
    .appearance(name)
    .then((appearance) => {
      if (!rowIsFresh(name, gen)) return;
      // A server that predates the beard, extras and badges sends none of them: they read as none.
      const sent: Partial<PeonAppearance> = appearance;
      looks.set(k, fromServer({ ...sent, beard: sent.beard ?? DEFAULT_BEARD, accessories: sent.accessories ?? [], badges: sent.badges ?? [] }));
      emit();
    })
    .catch(() => {
      /* the defaults stay; the next look-up tries again */
    })
    .finally(() => pending.delete(k));
}

/** Writes the given fields for a name; emits only on a change. */
function remember(name: string, look: Look): void {
  const k = key(name);
  if (!k) return;
  const before = looks.get(k);
  const next = { ...before, ...look };
  if (before && sameLook(before, next)) return;
  looks.set(k, next);
  emit();
}

/** Writes only the parts not known yet: a row painted from memory fills gaps and never overwrites. */
function seed(name: string, look: Look): void {
  const k = key(name);
  if (!k) return;
  const before = looks.get(k) ?? {};
  const next: Look = { ...before };
  let changed = false;
  for (const part of ["shirt", "hair", "beard", "accessories", "badges"] as const) {
    if (look[part] !== undefined && before[part] === undefined) {
      (next as Record<string, unknown>)[part] = look[part];
      changed = true;
    }
  }
  if (!changed) return;
  looks.set(k, next);
  emit();
}

/** A row's look (TopPeon, HallMonth) as the store keeps it: null is the default, an unknown value reads as the default, a part not carried stays unset. */
function fromRow(known: KnownLook | RowLook): Look {
  const look: Look = {};
  if (known.shirt !== undefined) look.shirt = isShirt(known.shirt) ? known.shirt : DEFAULT_SHIRT;
  if (known.hair !== undefined) look.hair = isHairstyle(known.hair) ? known.hair : DEFAULT_HAIR;
  if (known.beard !== undefined) look.beard = isBeard(known.beard) ? known.beard : DEFAULT_BEARD;
  if (known.accessories !== undefined) look.accessories = normalizeAccessories(Array.isArray(known.accessories) ? known.accessories : []);
  if (known.badges !== undefined) look.badges = normalizeWornBadges(Array.isArray(known.badges) ? known.badges : []);
  return look;
}

// A summary or Hall row that is at least as new as this device's own change (src/look-rows.ts).
setLookRowSink((row) => {
  const look = fromRow(row);
  if (Object.keys(look).length > 0) remember(row.name, look);
});

/**
 * Seeds a shirt the server already sent with something else (the Peon of the Month on the board
 * summary, a Hall of Fame winner), so the name is never fetched on its own. Emits only on a change.
 */
export function rememberShirt(name: string, shirt: Shirt): void {
  remember(name, { shirt });
}

/**
 * The look a row carries beside the name (TopPeon, HallMonth): each part is the server's value, null
 * when the peon never chose (so the default), or absent from a response that predates it.
 */
export interface KnownLook {
  shirt?: Shirt | null;
  hair?: Hairstyle | string | null;
  beard?: string | null;
  accessories?: string[] | null;
  badges?: string[] | null;
}

/**
 * The look of a peon whose look came with the data being shown (TopPeon, HallMonth). The row only
 * seeds the store before the first paint, filling the parts not known yet (null is the default, an
 * unknown value reads as the default); it never overwrites, because the row may be a board or Hall
 * painted from memory, older than the look this device just chose. Fresh rows reach the store when
 * their request answers (src/look-rows.ts), and the hook redraws from the store. A row that carries
 * none of the parts (a response that predates them) leaves the name to be looked up.
 */
export function useKnownLook(name: string | null, known: KnownLook | null | undefined): PeonAppearance {
  if (name && known) seedKnownLook(name, known);
  return useLook(name);
}

/** What useKnownLook does before the first paint: the parts not known yet come from the row. */
export function seedKnownLook(name: string, known: KnownLook): void {
  const look = fromRow(known);
  if (Object.keys(look).length > 0) seed(name, look);
}

/** The shirt alone, for a peon whose shirt came with the data being shown (see useKnownLook). */
export function useKnownShirt(name: string | null, known: Shirt | null | undefined): Shirt {
  return useKnownLook(name, { shirt: known }).shirt;
}

/** A change to a look: any of its parts, the rest kept. */
export type LookChange = Partial<PeonAppearance>;

/**
 * Records a change at once and tells the server, sending only the parts that changed (the server
 * keeps the rest); a failed save is put back and rethrown for the editor (a 403 "You don't own that
 * yet" among the reasons).
 */
export async function wear(name: string, change: LookChange): Promise<void> {
  const k = key(name);
  const before = looks.get(k);
  // Rows from summaries already on their way predate this change (src/look-rows.ts); so do those
  // that start before the server has it, hence the mark again once it answers.
  markLocalLook(name);
  looks.set(k, { ...before, ...change });
  emit();
  try {
    await api.peons.setAppearance(name, change);
    markLocalLook(name);
  } catch (e) {
    if (before === undefined) looks.delete(k);
    else looks.set(k, before);
    emit();
    throw e;
  }
}

export function setShirt(name: string, shirt: Shirt): Promise<void> {
  return wear(name, { shirt });
}

export function setHair(name: string, hair: Hairstyle): Promise<void> {
  return wear(name, { hair });
}

/** The look of a named peon (the defaults while it loads or with no name), kept current. */
export function useLook(name: string | null): PeonAppearance {
  const [look, setCurrent] = useState<PeonAppearance>(() => (name ? getLook(name) : DEFAULT_LOOK));
  useEffect(() => {
    if (!name) return;
    const update = () =>
      setCurrent((prev) => {
        const next = getLook(name);
        return sameLook(prev, next) ? prev : next;
      });
    listeners.add(update);
    update();
    load(name);
    return () => {
      listeners.delete(update);
    };
  }, [name]);
  return look;
}

export function useShirt(name: string | null): Shirt {
  return useLook(name).shirt;
}

export function useHair(name: string | null): Hairstyle {
  return useLook(name).hair;
}

// ---------- The signed-in peon's wardrobe ----------

/** The wardrobe as last read, for one name: what is owned, the gold held, whether pieces cost gold. */
export interface WardrobeState {
  name: string;
  owned: string[];
  balance: number;
  shop: boolean;
}

let wardrobe: WardrobeState | null = null;
const wardrobeListeners = new Set<() => void>();

function setWardrobe(next: WardrobeState | null): void {
  wardrobe = next;
  for (const listener of wardrobeListeners) listener();
}

export function getWardrobe(name: string): WardrobeState | null {
  return wardrobe && key(wardrobe.name) === key(name) ? wardrobe : null;
}

/** Reads the wardrobe and the look it carries (the server's word on both). */
export async function loadWardrobe(name: string): Promise<WardrobeState> {
  const gen = nextLookGeneration();
  const answer: Wardrobe = await api.peons.wardrobe(name);
  if (answer.look && rowIsFresh(name, gen)) remember(name, fromServer(answer.look));
  const next: WardrobeState = { name, owned: Array.isArray(answer.owned) ? answer.owned : [], balance: Number(answer.balance) || 0, shop: answer.shop === true };
  setWardrobe(next);
  return next;
}

/**
 * Buys one piece (POST /api/peons/wardrobe/buy): the owned list, the balance and the look come back
 * and replace what was here. Wearing it is the caller's next step (`wear`). A refusal (409 "Not
 * enough gold", or anything else) is rethrown after the wardrobe is read again, so the balance on
 * screen is the server's.
 */
export async function buyPiece(name: string, item: string): Promise<WardrobeState> {
  try {
    const answer = await api.peons.buy(name, item);
    if (answer.look) remember(name, fromServer(answer.look));
    const next: WardrobeState = { name, owned: answer.owned, balance: answer.balance, shop: getWardrobe(name)?.shop ?? true };
    setWardrobe(next);
    return next;
  } catch (e) {
    if (e instanceof ApiError && e.status !== 0) void loadWardrobe(name).catch(() => {});
    throw e;
  }
}

/**
 * The signed-in peon's wardrobe for the editor, read when it mounts. `error` is the server's words
 * when the read failed; the editor then behaves as the shop being off and lets the server refuse.
 */
export function useWardrobe(name: string): { wardrobe: WardrobeState | null; error: string | null; reload: () => void } {
  const [state, setState] = useState<WardrobeState | null>(() => getWardrobe(name));
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const update = () => setState(getWardrobe(name));
    wardrobeListeners.add(update);
    update();
    loadWardrobe(name).then(
      () => setError(null),
      (e: unknown) => setError(e instanceof Error ? e.message : "Could not load"),
    );
    return () => {
      wardrobeListeners.delete(update);
    };
  }, [name, tick]);
  return { wardrobe: state, error, reload: () => setTick((t) => t + 1) };
}

/** Tests only. */
export function resetAppearanceForTests(): void {
  looks.clear();
  pending.clear();
  wardrobe = null;
  resetLookRowsForTests();
}
